import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  ElementRef,
  ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TourService } from '@services/tour/tour.service';
import { TourStep } from '@services/tour/tour.types';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-tour-overlay',
  standalone: false,
  templateUrl: './tour-overlay.component.html',
  styleUrls: ['./tour-overlay.component.scss']
})
export class TourOverlayComponent implements OnInit, OnDestroy {
  @ViewChild('tooltipEl') tooltipEl!: ElementRef;

  isVisible = false;
  isPositioned = false;
  currentStep: TourStep | null = null;
  highlightRect: HighlightRect | null = null;
  tooltipStyle: { [key: string]: string } = {};
  tooltipText = '';
  stepCounter = '';
  isRtl = false;

  private destroy$ = new Subject<void>();
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private repositionTimeout: any = null;
  private documentClickListener: ((e: MouseEvent) => void) | null = null;

  constructor(
    private tourService: TourService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.isRtl = document.documentElement.dir === 'rtl' ||
      this.translate.getDefaultLang() === 'ar';

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.isRtl = event.lang === 'ar';
        this.updateTooltipText();
      });

    this.tourService.currentStep$
      .pipe(takeUntil(this.destroy$))
      .subscribe(step => {
        this.currentStep = step;
        this.isVisible = !!step;
        this.isPositioned = false;

        if (step) {
          this.updateTooltipText();
          this.updateStepCounter();
          this.removeDocumentClickListener();
          this.cleanupMutationObserver();

          // Wait one paint cycle for Angular to render the target element
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.positionHighlight();
              this.cdr.detectChanges();

              if (step.action === 'auto-advance' && step.autoAdvanceDelayMs) {
                this.tourService.scheduleAutoAdvance(step.autoAdvanceDelayMs);
              }

              if (step.action === 'wait-for-click' && step.targetSelector) {
                this.setupClickListener(step.targetSelector);
              }
            });
          });
        } else {
          this.highlightRect = null;
          this.cleanupObserver();
          this.cleanupMutationObserver();
          this.removeDocumentClickListener();
        }

        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupObserver();
    this.cleanupMutationObserver();
    this.removeDocumentClickListener();
    if (this.repositionTimeout) {
      clearTimeout(this.repositionTimeout);
    }
  }

  onNextClick(): void {
    this.tourService.advanceStep();
  }

  onSkipClick(): void {
    this.tourService.skipTour();
  }

  onBackdropClick(event: MouseEvent): void {
    if (!this.currentStep) return;

    // For wait-for-click, don't block - the click needs to reach the target
    if (this.currentStep.action === 'wait-for-click') {
      return;
    }
  }

  get showNextButton(): boolean {
    if (!this.currentStep) return false;
    return this.currentStep.action === 'highlight' || this.currentStep.action === 'wait-for-input';
  }

  get isLastStep(): boolean {
    return this.currentStep?.phase === 'COMPLETE';
  }

  private updateTooltipText(): void {
    if (this.currentStep) {
      this.tooltipText = this.translate.instant(this.currentStep.translationKey);
    }
  }

  private updateStepCounter(): void {
    const idx = this.tourService.currentGlobalStepIndex;
    const total = this.tourService.totalSteps;
    if (idx >= 0) {
      this.stepCounter = `${idx + 1} / ${total}`;
    }
  }

  private positionHighlight(): void {
    if (!this.currentStep?.targetSelector) {
      this.highlightRect = null;
      this.positionTooltipCenter();
      this.isPositioned = true;
      this.cdr.detectChanges();
      return;
    }

    const target = document.querySelector(this.currentStep.targetSelector);
    if (!target) {
      // Watch DOM for the element to appear instead of polling with setTimeout
      this.waitForElement(this.currentStep.targetSelector);
      return;
    }

    this.positionOnTarget(target as HTMLElement);
  }

  private waitForElement(selector: string): void {
    this.cleanupMutationObserver();

    const fallbackTimer = setTimeout(() => {
      this.cleanupMutationObserver();
      this.highlightRect = null;
      this.positionTooltipCenter();
      this.isPositioned = true;
      this.cdr.detectChanges();
    }, 3000);

    this.mutationObserver = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        clearTimeout(fallbackTimer);
        this.cleanupMutationObserver();
        this.ngZone.run(() => this.positionOnTarget(el as HTMLElement));
      }
    });

    this.mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  private positionOnTarget(target: HTMLElement): void {
    // Scroll into view if needed
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Use IntersectionObserver to detect when scroll completes and element is visible
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          observer.disconnect();
          clearTimeout(fallbackTimer);
          this.ngZone.run(() => this.applyPosition(target));
        }
      },
      { threshold: 0.5 }
    );

    // Fallback in case IntersectionObserver doesn't fire (element already visible)
    const fallbackTimer = setTimeout(() => {
      observer.disconnect();
      this.ngZone.run(() => this.applyPosition(target));
    }, 500);

    observer.observe(target);
  }

  private applyPosition(target: HTMLElement): void {
    const rect = target.getBoundingClientRect();
    this.highlightRect = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    };
    this.positionTooltipNearTarget(rect);
    this.setupObserver(target);
    this.isPositioned = true;
    this.cdr.detectChanges();
  }

  private positionTooltipCenter(): void {
    this.tooltipStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    };
  }

  private positionTooltipNearTarget(rect: DOMRect): void {
    const pad = 16;
    const tooltipWidth = 320;
    const position = this.currentStep?.tooltipPosition || 'bottom';

    switch (position) {
      case 'bottom':
        this.tooltipStyle = {
          position: 'fixed',
          top: `${rect.bottom + pad}px`,
          left: `${Math.max(pad, rect.left + rect.width / 2 - tooltipWidth / 2)}px`
        };
        break;
      case 'top':
        this.tooltipStyle = {
          position: 'fixed',
          top: `${rect.top - pad}px`,
          left: `${Math.max(pad, rect.left + rect.width / 2 - tooltipWidth / 2)}px`,
          transform: 'translateY(-100%)'
        };
        break;
      case 'left':
        this.tooltipStyle = {
          position: 'fixed',
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.left - pad}px`,
          transform: 'translate(-100%, -50%)'
        };
        break;
      case 'right':
        this.tooltipStyle = {
          position: 'fixed',
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.right + pad}px`,
          transform: 'translateY(-50%)'
        };
        break;
    }
  }

  private setupObserver(target: HTMLElement): void {
    this.cleanupObserver();
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        if (this.repositionTimeout) clearTimeout(this.repositionTimeout);
        this.repositionTimeout = setTimeout(() => this.positionHighlight(), 100);
      });
    });
    this.resizeObserver.observe(target);
  }

  private cleanupObserver(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  private cleanupMutationObserver(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  private setupClickListener(targetSelector: string): void {
    this.removeDocumentClickListener();
    // Use setTimeout to avoid capturing the current click
    setTimeout(() => {
      this.documentClickListener = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const matchEl = document.querySelector(targetSelector);
        if (matchEl && (matchEl === target || matchEl.contains(target))) {
          // Small delay to let the original click handler run first
          setTimeout(() => {
            this.ngZone.run(() => {
              this.tourService.advanceStep();
            });
          }, 200);
        }
      };
      document.addEventListener('click', this.documentClickListener, true);
    }, 100);
  }

  private removeDocumentClickListener(): void {
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener, true);
      this.documentClickListener = null;
    }
  }
}
